import { UserData } from '@/common/account/UserData';
import { SystemInfo } from '@/twapp/objects/SystemInfo';
import { IApiPromise } from '@/twapp/server/ApiService';
import { FileEndpoint } from '@/twapp/server/FileEndpoint';
import { createContext, useContext, useEffect, useState } from 'react';

export enum TromanSystem {
    Troman,
    Valman,
}

export interface ITromanContext {
    tromanSystem: TromanSystem;
    dialog: IDialogService;
    navigation: INavigationService;
    getSystemInfo: () => SystemInfo;
    apiServer: IApiServer;
    download: IDownloadHelper;
    userServer: IUserServer;
}

export interface IApiServer {
    Files: FileEndpoint;
}

export interface IUserServer {
    getAllUsers: () => IApiPromise<UserData[]>;
}

export interface IDownloadHelper {
    downloadBlob: (blob: Blob, filename: string) => void;
    downloadText: (text: string, filename: string) => void;
}

export const TromanContext = createContext<ITromanContext|null>(null);

export const useTromanService = () => {
    const context = useContext(TromanContext);
    if (context == null) {
        throw new Error('Missing TromanContext');
    }
    return context;
};

export interface IDialogService {    
    confirm: (message: string | string[], title?: string) => Promise<boolean>;
    notify: (message: string | string[], title?: string) => Promise<boolean>;
    warning: (message: string | string[]) => Promise<boolean>;
    error: (err: any) => Promise<boolean>;
}

export const useTromanSystem = () => {
    return useTromanService().tromanSystem;
};

export const useDialog = () => {
    return useTromanService().dialog;
};

export type NavigationGuardCallback = () => boolean;
export type NavigationGuardCleanup = () => void;
export type LockObject = { lock: (locked: boolean) => void, cleanup: NavigationGuardCleanup };

export interface INavigationOptions {
    reload?: boolean;
    location?: 'replace';
    inherit?: boolean;
}

export interface INavigationTargetMeeting {
    destination: 'meeting.details' | 'meeting.attendance' | 'meeting.payroll',
    params: { meetingId: string },
}

export type NavigationTarget = INavigationTargetMeeting;

export interface INavigationService {
    guard: (callback: NavigationGuardCallback) => NavigationGuardCleanup;
    addLock: (initial: boolean) => LockObject;
    navigateTo: (target: NavigationTarget, ignoreLock?: boolean, options?: INavigationOptions) => void;
}

export const useNavigationLock = (locked: boolean) => {
    const nav = useTromanService().navigation;
    const [lockObject, setLockObject] = useState<LockObject>();

    useEffect(() => {
        const lockInstance = nav.addLock(locked);
        setLockObject(lockInstance);
        return lockInstance.cleanup;
    }, []);

    useEffect(() => {
        lockObject && lockObject.lock(locked);
    }, [locked, lockObject]);
};

export const useNavigation = (): Pick<INavigationService, "navigateTo"> => {
    return useTromanService().navigation;
}

export const useSystemInfo = () => {
    const tc = useTromanService();
    return tc.getSystemInfo();
};

export const useApiServer = () => {
    const tc = useTromanService();
    return tc.apiServer;
};

export const useUserServer = () => {
    const tc = useTromanService();
    return tc.userServer;
}

export const useDownload = () => {
    const tc = useTromanService();
    return tc.download;
};